/**
 * アップロード再送・並列度の意思決定ロジック（DOM / API クライアント非依存）。
 *
 * upload-client.ts の XHR 実装から切り出し、env や supabase に依存させない。
 * これにより純粋なユニットテストが可能になり、責務も分離される。
 */

const MB = 1024 * 1024
const GB = 1024 * MB

/** PUT 1 回分の失敗。`retryable` で一時的（再送する価値がある）か区別する。 */
export class PutAttemptError extends Error {
  constructor(
    message: string,
    readonly retryable: boolean,
    readonly status?: number,
  ) {
    super(message)
    this.name = 'PutAttemptError'
  }
}

/** HTTP ステータスが一時的（リトライ対象）か。5xx / 408(timeout) / 429(rate limit)。 */
export function isRetryableStatus(status: number): boolean {
  return status >= 500 || status === 408 || status === 429
}

/** PUT のリトライ回数（初回 + 2 リトライ）と基準バックオフ。 */
export const MAX_PUT_ATTEMPTS = 3
const RETRY_BASE_DELAY_MS = 500

/**
 * 指数バックオフ + フルジッタ。attempt は 0 始まり。
 * モバイル回線の瞬断は同時多発しがちなので、ジッタで再送の同期集中を避ける。
 */
export function backoffDelayMs(
  attempt: number,
  baseMs: number = RETRY_BASE_DELAY_MS,
  rand: () => number = Math.random,
): number {
  const exp = baseMs * 2 ** attempt
  return Math.round(exp * (0.5 + rand() * 0.5)) // 50%〜100%
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }
    const onAbort = () => {
      clearTimeout(id)
      reject(new DOMException('Aborted', 'AbortError'))
    }
    const id = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/** 一時的失敗のみ true。AbortError と retryable=false（4xx / ETag 欠落）は再試行しない。 */
function isRetryableError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return false
  return err instanceof PutAttemptError && err.retryable
}

/**
 * 一時的失敗（ネットワーク断 / 5xx 等）のみ指数バックオフで再試行する。
 * - AbortError（ユーザーキャンセル）と retryable=false は即座に投げる
 * - キャンセル済みなら再試行しない（AbortSignal を尊重）
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  opts: {
    attempts: number
    baseDelayMs?: number | undefined
    signal?: AbortSignal | undefined
    rand?: (() => number) | undefined
  },
): Promise<T> {
  let lastErr: unknown
  for (let attempt = 0; attempt < opts.attempts; attempt++) {
    if (opts.signal?.aborted) throw new DOMException('Aborted', 'AbortError')
    try {
      return await fn()
    } catch (err) {
      if (!isRetryableError(err)) throw err
      lastErr = err
      if (attempt === opts.attempts - 1) break
      await delay(backoffDelayMs(attempt, opts.baseDelayMs, opts.rand), opts.signal)
    }
  }
  throw lastErr
}

type UaLike = { userAgentData?: { mobile?: boolean }; userAgent?: string }

/**
 * モバイル端末かを判定する。User-Agent Client Hints の `mobile` を優先し、
 * 無ければ UA 文字列でフォールバック。
 */
export function isMobileClient(nav?: UaLike): boolean {
  const n = nav ?? (typeof navigator !== 'undefined' ? (navigator as UaLike) : undefined)
  if (!n) return false
  if (typeof n.userAgentData?.mobile === 'boolean') return n.userAgentData.mobile
  return /Android|iPhone|iPad|iPod|Mobile/i.test(n.userAgent ?? '')
}

/**
 * ファイルサイズに応じて並列数を動的に決める。
 *
 * サーバ側の `pickPartSize` と対で考えること。
 *   〜 100MB    : 16MB × 並列 4 →  64MB 同時 in-flight
 *   〜 1GB      : 64MB × 並列 6 → 384MB 同時 in-flight
 *   〜 5GB      : 128MB × 並列 8 → 1GB  同時 in-flight
 *   5GB 〜      : 256MB × 並列 8 → 2GB  同時 in-flight
 *
 * 並列度を上げすぎると弱い回線では逆に詰まるため、8 を上限にしている。
 * モバイル回線は接続リセット（`net::ERR_CONNECTION_RESET`）が起きやすく、並列が高いほど
 * 誘発・再送コストが増えるため、端末がモバイルなら 2 に抑える。
 */
export function pickParallelism(sizeBytes: number, mobile: boolean = isMobileClient()): number {
  if (mobile) return 2
  if (sizeBytes < 100 * MB) return 4
  if (sizeBytes < 1 * GB) return 6
  return 8
}
